import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { CustomDataBaseModel, FieldType, UserModel } from '@seahorse/domain';
import {
  BadgeDirective,
  CopyContentButtonComponent,
  FieldValuePipe,
  LoadingIndicatorComponent,
  StatusProgressPipe,
} from '@seahorse/ui';
import { FormComponent } from '../../form/form.component';
import { FormModel } from '../../form/form.model';
import { DataCollectionConfig } from '../data-collection.model';
import { DataField } from '../data.model';
import { EmptyCollectionTemplate } from '../empty-collection.template';
import { InlineFormActionsTemplate } from '../../form/inline-form-actions.template';
import { deepCompare } from '@seahorse/common';
import { uniqueId } from 'underscore';
import { Observable } from 'rxjs';
import { RowAction } from '../../table/models/table-actions.model';

type index = number;

@Component({
  selector: 'temp-data-list',
  templateUrl: './data-list.component.html',
  styleUrls: ['./data-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    CopyContentButtonComponent,
    FormsModule,
    EmptyCollectionTemplate,
    FieldValuePipe,
    StatusProgressPipe,
    BadgeDirective,
    TranslateModule,
    FormComponent,
    LoadingIndicatorComponent,
    InlineFormActionsTemplate,
  ],
  providers: [FieldValuePipe, StatusProgressPipe],
})
export class DataListComponent<
  TData extends CustomDataBaseModel = CustomDataBaseModel
> {
  @ContentChild('rowAction', { read: TemplateRef })
  rowActionTemplate?: TemplateRef<any>;

  @ViewChildren(FormComponent) forms: QueryList<FormComponent>;

  @Input() actions?: RowAction[];

  private _config?: DataCollectionConfig<TData>;
  @Input() set config(val: DataCollectionConfig<TData>) {
    if (!val) {
      return;
    }

    this._config = val;

    this.fields =
      this._config.displayFields?.sort(
        (a, b) =>
          (a.order == null ? Number.MAX_VALUE : a.order) -
          (b.order == null ? Number.MAX_VALUE : b.order)
      ) ?? [];

    if (this._config.objectDefinition?.objectFieldDefinitions) {
      this.fields.map((x) => {
        const fieldDef =
          this._config.objectDefinition.objectFieldDefinitions.find(
            (f) => f.systemCode === x.key
          );
        x.showCopyButton = fieldDef?.additionalDataModel?.showCopyButton;
        return x;
      });
    }
  }

  get config() {
    return this._config;
  }

  get showCheckboxes() {
    return this.checkMode && this.checkChange.observers.length > 0;
  }

  @Input() isAllChecked = false;
  @Input() items: TData[];
  @Input() suggestedRows: Record<index, Observable<TData>> = {};
  @Input() users: UserModel[];
  @Input() selectable = true;
  @Input() singleSelect = false;
  @Input() title: string;
  @Input() emptyPlaceholderConfig: {
    size: string;
    message: string;
    icon: string;
  };
  // remove this property after refactor the selection logic, don't forgot remove the usage of this property at inbox-overview.page.html (2x)
  @Input() setSelectedUI = false;
  @Input() editModeTrigger: 'checkbox' | 'click' = 'click';
  @Input() showFormActions = true;
  @Input() checkMode = true;

  private _isLoading = false;

  @Input()
  set isLoading(value: boolean) {
    this._isLoading = value;
    this.cdr.markForCheck();
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  @Output() action = new EventEmitter<{
    action: string;
    data: TData;
  }>();
  @Output() checkChange = new EventEmitter<TData[]>();
  @Output() itemSelect = new EventEmitter<TData>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() submitRow = new EventEmitter<TData>();
  @Output() cancelRow = new EventEmitter<TData>();

  selectedItem: TData;
  fields: DataField[];
  FieldType = FieldType;
  hideActions = false;
  uniqueId = uniqueId('sh-');

  private cdr = inject(ChangeDetectorRef);
  private _fieldValuePipe = inject(FieldValuePipe);
  @Input() formModel?: FormModel;

  get checkedItems() {
    return this.items?.filter((item) => item['isChecked'] === true);
  }

  get showWarningColumn(): boolean {
    if (Array.isArray(this.items)) {
      return this.items?.some((item) => item && !!item['warningMessage']);
    }
    return false;
  }

  isRowReadonly(row: any) {
    return (
      this.formModel == null ||
      (this.formModel &&
        this.editModeTrigger === 'checkbox' &&
        !row['isChecked']) ||
      (this.formModel &&
        this.editModeTrigger === 'click' &&
        this.selectedItem !== row)
    );
  }

  itemAction(action: string, item: TData) {
    this.action.emit({ action, data: item });
  }

  selectItem(item: TData) {
    this.hideActions = false;
    if (!this.selectable) {
      return;
    }

    if (this.setSelectedUI) {
      this.selectedItem = item;
      this.hideActions = true;
    }
    this.itemSelect.emit(item);
  }

  checkAll() {
    if (this.singleSelect) {
      return;
    }

    this.items.forEach((item) => (item['isChecked'] = this.isAllChecked));
    this.checkChange.emit(this.checkedItems);
  }

  toggleChecked(item: TData) {
    if (item['isChecked'] && this.singleSelect) {
      this.checkedItems.forEach((i) => {
        if (i !== item) {
          i['isChecked'] = false;
        }
      });
    }

    this.checkChange.emit(this.checkedItems);
  }

  prepareItemsForExport() {
    const data = [];

    this.items.forEach((row) => {
      const preparedRow = [];

      this.fields.forEach((item) => {
        preparedRow.push(
          this._fieldValuePipe.transform(
            row[item.key],
            item.type,
            item.pipeModels
          )
        );
      });

      data.push(preparedRow);
    });

    return {
      title: this.title,
      data: data,
      fields: this.fields.map((x) => x.name),
    };
  }

  getTaskProgressFraction(taskStatus): number {
    if (taskStatus && taskStatus.Total > 0) {
      if (taskStatus.Progress === 0) {
        return 1;
      }

      if (
        taskStatus.Progress === taskStatus.Total &&
        taskStatus.Progress / taskStatus.Total === 1
      ) {
        return 0;
      }

      return taskStatus.Progress / taskStatus.Total;
    }
    return 1;
  }

  submitInlineForm(originalValue: TData, newValue: TData) {
    this.hideActions = false;
    const areThereChanges = !deepCompare(originalValue, newValue);

    if (!areThereChanges) {
      this.cancelInlineForm(originalValue);
      return;
    }

    this.submitRow.emit(newValue);
  }

  cancelInlineForm(originalValue: TData) {
    this.hideActions = false;
    this.cancelRow.emit(originalValue);
  }

  onCloseInlineForm(originalValue: TData, newValue: TData) {
    const areThereChanges = !deepCompare(originalValue, newValue);

    if (areThereChanges) {
      this.submitRow.emit(newValue);
    }
  }

  detectChanges() {
    this.cdr.detectChanges();
  }
}
